import { PlusOutlined } from '@ant-design/icons';
import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { ComponentPreview } from '@client/components/_ui/display/ComponentPreview';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tooltip } from 'antd';
import { useState } from 'react';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/claims/task-lists/search/$taskListId/tasks',
)({
    staticData: {
        metadata: {
            title: 'Task list',
        },
        ui: {
            title: 'Task list',
        },
        presentation: 'overlay',
        modules: ['claim'],
    },
    params: {
        parse: ({ taskListId }) => ({ taskListId: BigInt(taskListId) }),
    },
    component: TaskDefinitions,
});

type QueryParams = RouterInputs['taskDefinition']['listTaskDefinitions'];
type TaskDefinitionRow = RouterOutputs['taskDefinition']['listTaskDefinitions']['rows'][number];
type TaskDefinitionOrderBy = NonNullable<QueryParams['orderBy']>;

const columnHelper = createColumnHelper<TaskDefinitionRow, TaskDefinitionOrderBy>();

function TaskDefinitions() {
    const { trpc, trpcUtils } = useTrpc();
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { taskListId } = Route.useParams();
    const [queryParams, setQueryParams] = useState<RouterInputs['taskDefinition']['listTaskDefinitions']>({
        limit: 10,
        offset: 0,
        orderBy: [],
    });
    const {
        data: taskDefinitions,
        isPending,
        error,
    } = trpc.taskDefinition.listTaskDefinitions.useQuery({
        ...queryParams,
        where: {
            ...queryParams.where,
            taskListId,
        },
    });
    const { data: taskList } = trpc.taskList.getTaskList.useQuery({ id: taskListId });
    const { mutate: deleteTaskDefinition } = trpc.taskDefinition.deleteTaskDefinition.useMutation();

    const columns = [
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Task Name',
            cell: ({ value }) => (
                <div className="max-w-xs truncate">
                    <span>{value}</span>
                </div>
            ),
        }),
        columnHelper.accessor('description', {
            id: 'description',
            header: 'Task Description',
            cell: ({ value }) => <ComponentPreview>{value}</ComponentPreview>,
        }),
        columnHelper.accessor('dueDateOffset', {
            id: 'due-date-offset',
            header: 'Dynamic Due Date',
            cell: ({ value }) => `${value} ${value === 1 ? 'day' : 'days'}`,
            width: 150,
        }),
        columnHelper.actions({
            cell: ({ row }) => (
                <Button.Delete
                    onConfirm={(callback) => {
                        message.loading('Deleting task...');
                        deleteTaskDefinition(
                            { id: row.id },
                            {
                                onSuccess: () => {
                                    message.success('Task deleted successfully');
                                    callback();
                                    trpcUtils.taskDefinition.invalidate();
                                },
                                onError(error) {
                                    message.destroy();
                                    errorMessage.show(error);
                                },
                            },
                        );
                    }}
                />
            ),
        }),
    ];

    const tableProps = usePrismaTable<TaskDefinitionRow, QueryParams['where'], TaskDefinitionOrderBy>({
        data: taskDefinitions?.rows || [],
        total: taskDefinitions?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams,
        onParamsChange: setQueryParams,
        // A descriptive key: the task lists page underneath this overlay holds `q` (CUR-1341).
        urlKey: 'tasks',
        queryError: error,
        emptyState: { noRecordsTitle: 'No tasks in this task list yet' },
        pagination: {
            position: 'both',
        },
    });

    return (
        <PageController route={Route}>
            <OverlayPage
                title={
                    <div title={taskList?.name} className="max-w-[600px] truncate">
                        {taskList?.name ?? 'Tasks'}
                    </div>
                }
                size="xLarge"
                isDirty={() => false}
                afterClose={() =>
                    navigate({
                        to: '/console/$orgId/configuration/claims-products/claims/task-lists/search',
                        params: { orgId },
                    })
                }
                extra={
                    <Tooltip title="Create new task">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() =>
                                navigate({
                                    to: '/console/$orgId/configuration/claims-products/claims/task-lists/search/$taskListId/tasks/add',
                                    params: { orgId, taskListId },
                                })
                            }
                        >
                            Add New Task
                        </Button>
                    </Tooltip>
                }
                footer={({ requestClose }) => (
                    <DrawerFooter showOk={false} cancelText="Close" onCancel={requestClose} />
                )}
            >
                <Outlet />
                <PrismaTable
                    {...tableProps}
                    size="small"
                    onRow={(record) => ({
                        className: 'cursor-pointer',
                        onClick: () => {
                            navigate({
                                to: '/console/$orgId/configuration/claims-products/claims/task-lists/search/$taskListId/tasks/edit/$id',
                                params: { orgId, taskListId, id: record.id },
                            });
                        },
                    })}
                />
            </OverlayPage>
        </PageController>
    );
}
